import { useState, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { formatTatDuration, calculateTatMinutes, parseDateClean } from 'utils/tatUtils';

/**
 * Custom hook to compute live Elapsed Time (ET).
 * 
 * Logic:
 * - If startedOn is null/empty -> returns null
 * - If completedOn is provided -> computes static TAT between startedOn and completedOn (no timer)
 * - If in-progress (startedOn provided, completedOn empty):
 *    - Computes synchronously on every render (instant reactive updates on date/time change)
 *    - Automatically re-ticks every minute to keep time fresh
 *    - Cleans up timer and listeners on unmount
 */
export function useLiveTat(
    startedOn: string | Date | Dayjs | null | undefined,
    completedOn?: string | Date | Dayjs | null | undefined
): string | null {
    const hasStarted = Boolean(startedOn && parseDateClean(startedOn)?.isValid());
    const isCompleted = Boolean(completedOn && parseDateClean(completedOn)?.isValid());

    const [, setTick] = useState(0);

    useEffect(() => {
        if (!hasStarted || isCompleted) {
            return;
        }

        let intervalId: any;
        let timeoutId: any;

        const scheduleMinuteSync = () => {
            setTick((t) => (t + 1) % 1000000);

            const now = dayjs();
            const msToNextMinute = (60 - now.second()) * 1000 - now.millisecond() + 200;

            timeoutId = setTimeout(() => {
                setTick((t) => (t + 1) % 1000000);
                intervalId = setInterval(() => {
                    setTick((t) => (t + 1) % 1000000);
                }, 60000);
            }, msToNextMinute);
        };

        scheduleMinuteSync();

        const handleWindowFocus = () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
            scheduleMinuteSync();
        };

        window.addEventListener('focus', handleWindowFocus);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [startedOn, isCompleted, hasStarted]);

    // Compute synchronously on every render
    if (!hasStarted) return null;

    const start = parseDateClean(startedOn);
    if (!start || !start.isValid()) return null;

    if (isCompleted) {
        const end = parseDateClean(completedOn);
        if (!end || !end.isValid()) return null;
        const mins = calculateTatMinutes(start, end);
        return mins !== null ? formatTatDuration(mins) : null;
    }

    const mins = calculateTatMinutes(start);
    return mins !== null ? formatTatDuration(mins) : null;
}

export default useLiveTat;

export function getLiveTat(
    startedOn: string | Date | Dayjs | null | undefined,
    completedOn?: string | Date | Dayjs | null | undefined
): string | null {
    const hasStarted = Boolean(startedOn && parseDateClean(startedOn)?.isValid());
    const isCompleted = Boolean(completedOn && parseDateClean(completedOn)?.isValid());

    if (!hasStarted) return null;

    const start = parseDateClean(startedOn);

    if (!start || !start.isValid()) return null;

    if (isCompleted) {
        const end = parseDateClean(completedOn);

        if (!end || !end.isValid()) return null;

        const mins = calculateTatMinutes(start, end);

        return mins !== null ? formatTatDuration(mins) : null;
    }

    const mins = calculateTatMinutes(start);

    return mins !== null ? formatTatDuration(mins) : null;
}

